import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { DataTable } from "../../components/DataTable";
import { Dialog, useToast } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { Field, Panel } from "../../components/Layout";
import { EmptyState, QueryState } from "../../components/States";

interface DefectType { defect_code: string; defect_name: string; category: string; measurement_unit: string | null; treatment_low: string | null; treatment_medium: string | null; treatment_high: string | null; active: number | null; description?: string | null }
interface AiRoute { asset_code: string; task_type: string; detector_route: string; model_key: string | null; notes: string | null; confidence_threshold: number; ai_enabled: number; model_ready: number }
interface Cat { defects: DefectType[]; assets: Record<string, unknown>[]; signs: Record<string, unknown>[]; ai_routes: AiRoute[] }

const EMPTY: DefectType = { defect_code: "", defect_name: "", category: "Pavement Surface Defect", measurement_unit: "", treatment_low: "", treatment_medium: "", treatment_high: "", active: 1 };

function DefectDialog({ value, onClose }: { value: DefectType | null; onClose: () => void }) {
  const [f, setF] = useState<DefectType>(value ?? EMPTY);
  const [error, setError] = useState<string | null>(null);
  const isNew = !value?.defect_code;
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api(`/api/admin/catalogues/defects/${encodeURIComponent(f.defect_code)}`, {
      method: "PUT",
      json: { defect_name: f.defect_name, category: f.category, description: f.description ?? "", measurement_unit: f.measurement_unit ?? "",
              treatment_low: f.treatment_low ?? "", treatment_medium: f.treatment_medium ?? "", treatment_high: f.treatment_high ?? "", active: !!Number(f.active ?? 1) },
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "catalogues"] }); qc.invalidateQueries({ queryKey: ["review", "catalogue"] }); toast({ text: `Saved ${f.defect_code}.` }); onClose(); },
    onError: (e) => setError(e instanceof Error ? e.message : "Could not save."),
  });
  const set = (k: keyof DefectType) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onClose={onClose} labelledBy="dt-title">
      <form className="dialog-body" style={{ width: "min(560px, calc(100vw - 32px))" }} onSubmit={(e) => { e.preventDefault(); setError(null); m.mutate(); }}>
        <h2 className="dialog-title" id="dt-title">{isNew ? "Add a defect type" : `Edit ${f.defect_code}`}</h2>
        <div className="form-grid">
          <Field label="Code" htmlFor="dt-code" hint="e.g. RAI-D02"><input id="dt-code" className="input" required readOnly={!isNew} value={f.defect_code} onChange={set("defect_code")} /></Field>
          <Field label="Name" htmlFor="dt-name"><input id="dt-name" className="input" required value={f.defect_name} onChange={set("defect_name")} /></Field>
          <Field label="Category" htmlFor="dt-cat"><input id="dt-cat" className="input" required value={f.category} onChange={set("category")} /></Field>
          <Field label="Measured in" htmlFor="dt-unit" hint="e.g. m, m², No."><input id="dt-unit" className="input" value={f.measurement_unit ?? ""} onChange={set("measurement_unit")} /></Field>
        </div>
        <Field label="Treatment when severity is Low" htmlFor="dt-tl"><input id="dt-tl" className="input" value={f.treatment_low ?? ""} onChange={set("treatment_low")} /></Field>
        <Field label="Treatment when Medium" htmlFor="dt-tm"><input id="dt-tm" className="input" value={f.treatment_medium ?? ""} onChange={set("treatment_medium")} /></Field>
        <Field label="Treatment when High" htmlFor="dt-th"><input id="dt-th" className="input" value={f.treatment_high ?? ""} onChange={set("treatment_high")} /></Field>
        <label className="switch"><input type="checkbox" checked={!!Number(f.active ?? 1)} onChange={(e) => setF({ ...f, active: e.target.checked ? 1 : 0 })} />Engineers can choose this type</label>
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        <div className="form-actions"><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={m.isPending}>Save</button></div>
      </form>
    </Dialog>
  );
}

function RouteRow({ r }: { r: AiRoute }) {
  const [thr, setThr] = useState(String(r.confidence_threshold));
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: (body: { confidence_threshold: number; ai_enabled: boolean }) => api(`/api/admin/catalogues/ai-routes/${encodeURIComponent(r.asset_code)}`, { method: "PATCH", json: body }),
    onSuccess: () => { qc.invalidateQueries(); toast({ text: `Updated ${r.asset_code}.` }); },
    onError: (e) => toast({ text: e instanceof Error ? e.message : "Could not update.", tone: "error" }),
  });
  const changed = Number(thr) !== Number(r.confidence_threshold);
  return (
    <div className="row" style={{ gap: 8 }}>
      <label className="sr-only" htmlFor={`thr-${r.asset_code}`}>Minimum confidence for {r.asset_code}</label>
      <input id={`thr-${r.asset_code}`} className="input num" style={{ width: 90 }} type="number" min="0.01" max="0.99" step="0.01" value={thr} onChange={(e) => setThr(e.target.value)} />
      {changed ? <button className="btn btn-sm btn-primary" disabled={m.isPending} onClick={() => m.mutate({ confidence_threshold: Number(thr), ai_enabled: !!Number(r.ai_enabled) })}>Save</button> : null}
      <label className="switch"><input type="checkbox" checked={!!Number(r.ai_enabled)} disabled={m.isPending}
             onChange={(e) => m.mutate({ confidence_threshold: Number(thr), ai_enabled: e.target.checked })} />{Number(r.ai_enabled) ? "On" : "Off"}</label>
    </div>
  );
}

export function CataloguesTab() {
  const q = useQuery({ queryKey: ["admin", "catalogues"], queryFn: () => api<Cat>("/api/admin/catalogues") });
  const [editing, setEditing] = useState<DefectType | null | undefined>(undefined);
  return (
    <QueryState query={q}>
      {(d) => (
        <>
          <Panel title={`Defect types (${d.defects.length})`} id="cat-def" flush actions={<button className="btn btn-sm btn-primary" onClick={() => setEditing(null)}><Icon name="plus" />Add defect type</button>}>
            <DataTable<DefectType>
              rows={d.defects}
              rowKey={(r) => r.defect_code}
              onRowClick={(r) => setEditing(r)}
              caption="Defect types"
              empty={<EmptyState title="No defect types" />}
              columns={[
                { key: "c", label: "Code", sort: (r) => r.defect_code, render: (r) => <span className="mono">{r.defect_code}</span> },
                { key: "n", label: "Name", sort: (r) => r.defect_name, render: (r) => r.defect_name },
                { key: "cat", label: "Category", render: (r) => r.category },
                { key: "t", label: "Treatment (Medium)", render: (r) => r.treatment_medium || <span className="muted">—</span> },
                { key: "a", label: "Status", render: (r) => <span className={`chip ${Number(r.active ?? 1) ? "verified" : "rejected"}`}>{Number(r.active ?? 1) ? "In use" : "Off"}</span> },
              ]}
            />
          </Panel>
          <Panel title="AI detection thresholds" id="cat-ai" flush>
            <DataTable<AiRoute>
              rows={d.ai_routes}
              rowKey={(r) => r.asset_code}
              caption="AI detection thresholds"
              columns={[
                { key: "a", label: "Route", sort: (r) => r.asset_code, render: (r) => <><span className="mono">{r.asset_code}</span>{r.notes ? <small className="muted" style={{ display: "block", maxWidth: 360 }}>{r.notes}</small> : null}</> },
                { key: "t", label: "Method", render: (r) => <>{r.task_type}<small className="muted" style={{ display: "block" }}>{r.detector_route}</small></> },
                { key: "r", label: "Model ready", render: (r) => (Number(r.model_ready) ? "Yes" : <span className="muted">No</span>) },
                { key: "c", label: "Minimum confidence · on/off", render: (r) => <RouteRow r={r} /> },
              ]}
            />
          </Panel>
          <p className="muted" style={{ fontSize: 13 }}>
            The processing worker runs the routes that are on and have a model ready, keeping detections at or above the minimum confidence; the same value decides which unreviewed findings viewers see. A route can find more than its own asset: the In-house AI, filed under DRN-03, also finds guardrails, manholes, signs and road damage. Asset classes ({d.assets.length}) and traffic signs ({d.signs.length}) can't be edited here yet.
          </p>
          {editing !== undefined ? <DefectDialog value={editing} onClose={() => setEditing(undefined)} /> : null}
        </>
      )}
    </QueryState>
  );
}
